import {
  Box,
  Table as TableDS,
  TableContainer,
  TablePagination,
} from '@cambridgeassessment/cambridge-ui';
import { CircularProgress } from '@material-ui/core';
import React, { memo, useCallback, useMemo } from 'react';
import { TooltipContent } from 'src/types';
import TableBody from './TableBody';
import TableBodyMessage from './TableBodyMessage';
import TableHead from './TableHead';
import TableLoader from './TableLoader';
import { HeadCell, Order, Row, Settings } from './types';
import { getComparator, stableSort } from './utils/sorting';

export const DEFAULT_ROW_HEIGHT = 56;
export const ROWS_PER_PAGE_OPTIONS = [15, 25, 50];

type Props = {
  headCells: HeadCell[];
  rows: Row[];
  sortColumns?: (
    order: Order,
    orderBy: string
  ) => (
    a: {
      [x: string]: unknown;
    },
    b: {
      [x: string]: unknown;
    }
  ) => number;
  settings: Settings;
  updateSettings: (value: Settings) => void;
  error?: boolean;
  loading?: boolean;
  tooltips?: TooltipContent;
  className?: string;
  noResultsMessage?: React.ReactNode;
  rowsPerPageOptions?: number[];
  rowHeight?: number;
  fixedRowHeight?: boolean;
  serverTotalCount?: number | null;
  loadingMore?: boolean;
  showEmptyRows?: boolean;
};

const Table: React.FC<Props> = ({
  headCells,
  rows,
  sortColumns,
  settings,
  updateSettings,
  loading,
  error,
  tooltips,
  className,
  noResultsMessage,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  rowHeight = DEFAULT_ROW_HEIGHT,
  fixedRowHeight = false,
  serverTotalCount = null,
  loadingMore = false,
  showEmptyRows = true,
}) => {
  const { order, orderBy, page, rowsPerPage } = settings;

  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
      updateSettings({ ...settings, page: newPage }),
    [updateSettings, settings]
  );

  const handleRequestSort = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      property: string
    ) => {
      const isAsc =
        settings.orderBy === property && settings.order === Order.Asc;
      updateSettings({
        ...settings,
        order: isAsc ? Order.Desc : Order.Asc,
        orderBy: property,
      });
    },
    [updateSettings, settings]
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      updateSettings({
        ...settings,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
      }),
    [updateSettings, settings]
  );

  const sortedRows = useCallback(
    () =>
      stableSort<Row>(
        rows,
        sortColumns
          ? sortColumns(order, orderBy)
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            getComparator<any>(order, orderBy)
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rows, rowsPerPage, sortColumns]
  );

  const emptyRows = useMemo(() => {
    const visibleRowCount = Math.min(
      rowsPerPage,
      Math.max(0, rows.length - page * rowsPerPage)
    );

    if (fixedRowHeight) {
      return Math.max(0, rowsPerPage - visibleRowCount);
    }

    return rowsPerPage < rows.length
      ? Math.max(0, rowsPerPage - visibleRowCount)
      : 0;
  }, [fixedRowHeight, rowsPerPage, rows.length, page]);

  const showLoadingSpinner = useMemo(() => {
    if (
      !loadingMore ||
      serverTotalCount == null ||
      serverTotalCount <= rows.length
    ) {
      return false;
    }
    const lastLoadedPage = Math.max(
      0,
      Math.ceil(rows.length / rowsPerPage) - 1
    );
    return page >= lastLoadedPage;
  }, [loadingMore, serverTotalCount, rows.length, rowsPerPage, page]);

  const labelDisplayedRows = useCallback(
    ({ from, to, count }: { from: number; to: number; count: number }) => {
      const label =
        loadingMore && serverTotalCount != null && serverTotalCount > count
          ? `${from}–${to} of ${count} (${serverTotalCount} total)`
          : `${from}–${to} of ${count}`;

      if (!showLoadingSpinner) {
        return label;
      }

      return (
        <Box
          component="span"
          display="inline-flex"
          alignItems="center"
          data-testid="table-pagination-displayed-rows"
        >
          {label}
          <CircularProgress
            size={16}
            data-testid="table-pagination-loading"
            style={{ marginLeft: 8 }}
          />
        </Box>
      );
    },
    [loadingMore, serverTotalCount, showLoadingSpinner]
  );

  const renderBody = useCallback(() => {
    if (loading) {
      return <TableLoader colSpan={headCells.length} />;
    }
    if (error) {
      return <TableBodyMessage error colSpan={headCells.length} />;
    }
    if (rows.length) {
      return (
        <TableBody
          rows={sortedRows()}
          emptyRows={showEmptyRows ? emptyRows : 0}
          colSpan={headCells.length}
          rowHeight={rowHeight}
          fixedRowHeight={fixedRowHeight}
          rowsPerPage={fixedRowHeight ? rowsPerPage : undefined}
        />
      );
    }
    if (!rows.length) {
      return (
        <TableBodyMessage
          colSpan={headCells.length}
          noResultsMessage={noResultsMessage}
        />
      );
    }
  }, [
    loading,
    error,
    emptyRows,
    fixedRowHeight,
    headCells.length,
    noResultsMessage,
    rowHeight,
    rows.length,
    rowsPerPage,
    sortedRows,
  ]);

  return (
    <Box>
      <TableContainer className={className}>
        <TableDS>
          <TableHead
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            tooltips={tooltips}
          />
          {renderBody()}
        </TableDS>
      </TableContainer>
      {rows.length > 0 && (
        <Box width="100%">
          <TablePagination
            component="div"
            count={rows.length}
            labelDisplayedRows={labelDisplayedRows}
            labelRowsPerPage="Rows per page"
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            data-testid="sorting-table-pagination"
            style={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(Table);
